import { Dropdown, Space, Tooltip } from "antd";
import moment from "moment";
import { Fragment, useEffect, useRef, useState } from "react";
import { BsArrowReturnLeft } from "react-icons/bs";
import {
  FaArrowDown,
  FaArrowUp,
  FaChevronLeft,
  FaChevronRight,
  FaFileInvoice,
} from "react-icons/fa";
import { MdBlock } from "react-icons/md";
import { TiArrowSortedDown } from "react-icons/ti";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { CustumedMatrix } from "tamtam-components";
import {
  getCollaboratorsSuggestions,
  getVolumetryCliets,
  updateNeutralClient,
} from "../../../api";
import _ from "../../../i18n";
import {
  formatAmount,
  getMonthShortLabelObject,
  getUserNameForAvatar,
  renderTeamYears,
  useOnClickOutside,
} from "../../../utils";
import TTPFilterHorizontalBar from "../../common/Filter/TTPFilterHorizontalBar";
import {
  IconArrowRight,
  InfoCircleFilledIcon,
  SettingsIcon,
} from "../../common/Icons";
import TTPPaginator from "../../common/TTPPaginator";
import DraggableDialog from "./DraggableDialog";
import InvoiceConfig from "./InvoiceConfig";
import InvoiceDownloader from "./InvoiceModal";
import InvoiceTracking from "./InvoiceTracking";
import NeutralReport from "./NeutralReport";
import "./PrestationSuivi.local.scss";

const PrestationSuivi = ({ lng }) => {
  const { token, navCommunity, loggedAs, spaceAs } = useSelector(
    (state) => state.auth
  );
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear;

  const activeMonthRef = useRef(null);
  const cardsWrapperRef = useRef(null);
  const [data, setData] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [totalData, setTotalData] = useState([]);
  const [nbrClients, setNbrClients] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(previousYear);
  const [yearDivIsVisible, setYearDivIsVisible] = useState(false);
  const [collaboratorOptions, setCollaboratorOptions] = useState([{}]);
  const [selectedCollaborator, setSelectedCollaborator] = useState({});
  const [showSideBar, setShowSideBar] = useState({
    invoiceBar: false,
    neutralBar: false,
    configBar: false,
  });
  const [neutral, setNeutral] = useState({});

  const monthsArray = getMonthShortLabelObject(lng);
  const previousMonthData = monthsArray[previousMonth];

  const [filter, setFilter] = useState({
    collaborator: selectedCollaborator?.value,
    pageSize: 50,
    paginationPage: 1,
    tabs: monthsArray,
    period: previousMonthData,
    start: `${year}-${previousMonthData.start}`,
    end: `${year}-${previousMonthData.end}`,
  });
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState({
    max_billing: null,
    "16.67%": null,
    task: null,
    volumetry: null,
    billingRate: null,
    encoding: null,
    closure: null,
  });

  const ref = useRef();
  const scrollRef = useRef(null);
  useOnClickOutside(ref, () => setYearDivIsVisible(false));

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -500, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 500, behavior: "smooth" });
    }
  };

  const scrollCards = (direction) => {
    const wrapper = cardsWrapperRef.current;
    const scrollAmount = 500;

    if (direction === "left") {
      wrapper.scrollBy({ left: -scrollAmount, behavior: "smooth" });
    } else {
      wrapper.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (!data.length) return;

    const totalRow = data[0];
    const dataToSort = data.slice(1);

    const sortedRows = dataToSort
      .filter((client) => client.client_id !== "%")
      .sort((a, b) => {
        const sortKeys = Object.keys(sortBy);

        for (let key of sortKeys) {
          const sortOrder = sortBy[key];
          if (!sortOrder) continue;

          let valueA, valueB;

          if (key === "max_billing" || key === "16.67%") {
            valueA = a.ceiling_compliance_1_6?.[key];
            valueB = b.ceiling_compliance_1_6?.[key];
          } else if (key === "task") {
            valueA = a.tasks?.[1]?.task_duration;
            valueB = b.tasks?.[1]?.task_duration;
          } else if (key === "volumetry") {
            valueA = a.volumetry["total"];
            valueB = b.volumetry["total"];
          } else if (key === "billingRate") {
            valueA = a.billing_rate["value"];
            valueB = b.billing_rate["value"];
          } else if (key === "encoding" || key === "closure") {
            valueA = a.type[key];
            valueB = b.type[key];
          }

          valueA = valueA === "-" ? null : parseFloat(valueA);
          valueB = valueB === "-" ? null : parseFloat(valueB);

          if (isNaN(valueA)) valueA = null;
          if (isNaN(valueB)) valueB = null;

          if (valueA === null && valueB === null) continue;
          if (valueA === null) return sortOrder === "ASC" ? 1 : -1;
          if (valueB === null) return sortOrder === "ASC" ? -1 : 1;

          const comparison = valueA - valueB;

          if (comparison !== 0) {
            return sortOrder === "ASC" ? comparison : -comparison;
          }
        }

        return 0;
      });

    setData([totalRow, ...sortedRows]);
  }, [sortBy]);

  useEffect(() => {
    if (activeMonthRef.current) {
      activeMonthRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [filter.period?.id]);

  useEffect(() => {
    const rows = document.querySelectorAll(
      ".CustumedMatrix_matrix_body_line__11lbb"
    );

    rows.forEach((row, index) => {
      if (index != 0 && index === selectedRow) {
        row.style.backgroundColor = "#18a0fb50";
      } else {
        row.style.backgroundColor = "#fff";
      }
    });
  }, [selectedRow]);

  const fetchVolumetryClients = async () => {
    if (token && navCommunity && filter.collaborator) {
      try {
        setLoading(true);

        const filterSubPeriodStart = filter.subPeriod?.start || "01-01";
        const formattedDate = `${year}-${filterSubPeriodStart}`;

        const response = await getVolumetryCliets(
          token,
          navCommunity.id,
          formattedDate,
          filter
        );

        const responseData = Object.values(response?.data?.data)[0] || [];
        const { data: dataObject, ...rest } = responseData;

        setData(dataObject?.pagination);
        setTotalData(dataObject?.pagination[0]);
        setNbrClients(dataObject?.totalCount || []);
        setMetaData(rest);
      } catch (e) {
        console.error("Error fetching volumetry clients:", e);
        setData([]);
      } finally {
        setLoading(false);
      }
    }
  };

  useQuery(
    ["getVolumetryCliets", token, navCommunity, filter, year],
    fetchVolumetryClients,
    {
      enabled: !!token && !!navCommunity,
    }
  );

  const fetchListCollaborators = async () => {
    if (token && navCommunity) {
      try {
        const response = await getCollaboratorsSuggestions(
          token,
          navCommunity.id
        );
        let data = response.data.data
          .filter(
            (item) =>
              item !== null &&
              item.zone === 2 &&
              !["175", "202"].includes(item.erpId)
          )
          .map((t) => {
            const [firstName, lastName] = t.name.split(" ");
            return {
              value: t.id,
              label: t.name,
              grade: t.grade,
              code: t.oamCode,
              signature: getUserNameForAvatar(firstName, lastName),
            };
          });

        data.sort((a, b) => {
          if (!a.code) return 1;
          if (!b.code) return -1;

          const matchA = a.code.match(/^(\d+)([a-zA-Z])$/);
          const matchB = b.code.match(/^(\d+)([a-zA-Z])$/);

          if (!matchA || !matchB) return 0;

          const [numA, letterA] = matchA.slice(1);
          const [numB, letterB] = matchB.slice(1);

          if (letterA !== letterB) return letterA.localeCompare(letterB);

          return Number(numA) - Number(numB);
        });

        setSelectedCollaborator(data[0]);
        setFilter({ ...filter, collaborator: data[0].value });
        setCollaboratorOptions(data);
      } catch (e) {
        return null;
      }
    }
  };

  useQuery(["getCollaborators", token, navCommunity], fetchListCollaborators);

  const handleCollaboratorsChange = (item) => {
    const selected = collaboratorOptions.filter(
      (c) => c.value === parseInt(item)
    );
    setSelectedCollaborator(selected[0]);
    setFilter({ ...filter, collaborator: parseInt(item) });
  };

  const handleStartDateChange = (value) => {
    const startYear = new Date(value).getFullYear();
    const newEnd =
      filter.end ||
      new Date(new Date(value).setMonth(new Date(value).getMonth() + 1));

    if (startYear !== year) {
      setYear(startYear);
    }

    setFilter({
      ...filter,
      start: value,
      end: newEnd,
    });
  };

  const billingRateLabel =
    selectedCollaborator?.grade === "SENIOR"
      ? "14 €"
      : selectedCollaborator?.grade === "MEDIOR"
      ? "11.5 €"
      : selectedCollaborator?.grade === "JUNIOR"
      ? "8.5 €"
      : selectedCollaborator?.grade === "ADMIN"
      ? "5 €"
      : "-";

  const columnsClientsInformations = [
    {
      id: "period",
      label: "Période",
      isEditable: true,
      widthPercentage: 10,
    },
    {
      id: "type",
      label: "Type",
      isEditable: true,
      widthPercentage: 6,
    },
    {
      id: "volumetry",
      label: "Caractéristiques encodage",
      isEditable: true,
      widthPercentage: 16,
    },
    {
      id: "tasks",
      label: "Time Sheet",
      isEditable: true,
      widthPercentage: 26,
    },
    {
      id: "billing_rate",
      label: billingRateLabel,
      isEditable: true,
      widthPercentage: 4,
    },
    {
      id: "ceiling_compliance_1_6",
      label: "Plafond en respect de la norme 1/6",
      isEditable: true,
      widthPercentage: 26,
    },
  ];

  const filters = [
    ["MANAGER", "ADMIN", "SUPERADMIN"].includes(loggedAs) &&
      ["MANAGER", "ADMIN", "SUPERADMIN"].includes(spaceAs) && {
        type: "SELECT",
        options: {
          isSimple: true,
          isSearch: true,
          isMulti: false,
          isClearable: true,
          placeholder: _("collaborator"),
          options: collaboratorOptions,
          values: filter.collaborator,
          action: handleCollaboratorsChange,
        },
      },
  ];

  const renderPaginate = () => {
    return (
      <TTPPaginator
        onPageChange={({ selected }) =>
          setFilter({ ...filter, paginationPage: selected + 1 })
        }
        pageSize={filter.pageSize}
        nbResult={nbrClients}
        paginationPage={filter.paginationPage}
      />
    );
  };

  const convertToHoursMinutes = (value) => {
    const hours = Math.floor(value).toLocaleString();
    const minutes = Math.round((value - Math.floor(value)) * 60);
    return `${hours}h ${minutes}m`;
  };

  const handleInputChange = (value, line) => {
    const newNeutral = {
      ...neutral,
      [filter.collaborator]: {
        ...neutral[filter.collaborator],
        [line.client_id]: value == "x",
      },
    };
    setNeutral(newNeutral);
  };

  const handleOnEnter = (event) => {
    if (event.key === "Enter") {
      handleSave();
    }
  };

  const handleSave = () => {
    const month = filter.start
      ? new Date(filter.start).getMonth() + 1
      : new Date().getMonth() + 1;
    const data = {
      token,
      organization: navCommunity && navCommunity.id,
      year,
      month,
      neutral: JSON.stringify(neutral),
    };

    updateNeutralClient(data)
      .then((resp) => {
        if (resp.data.result === "OK") {
          toast.success(_("successfully_updated"));
          fetchVolumetryClients();
        }
      })
      .catch((e) => {
        if (
          e.response &&
          e.response.data &&
          e.response.data.errors &&
          e.response.data.errors.length > 0
        )
          toast.error(_(e.response.data.errors[0].message));
        else toast.error(_("error"));
      });
  };

  const toggleSideBar = (key) => {
    setShowSideBar((prevShowSideBar) => ({
      ...prevShowSideBar,
      [key]: !prevShowSideBar[key],
    }));
  };

  const handleSort = (key) => {
    setSortBy((prev) => {
      const newSort = {};
      Object.keys(prev).forEach((k) => {
        newSort[k] = null;
      });

      newSort[key] = prev[key] === "ASC" ? "DESC" : "ASC";
      return newSort;
    });
  };

  const handleColumns = (column, line) => {
    const keys = Object.keys(sortBy);
    const rowIndex = data.findIndex((row) => row.client_id === line.client_id);
    switch (column.id) {
      case "period":
        return (
          <div
            className="column"
            style={{ margin: "0 auto", width: "100%" }}
            onClick={() => setSelectedRow(rowIndex)}
          >
            {Object.keys(line.period).map((key, index) => (
              <div
                className="labled-tag no-border"
                key={index}
                style={{ textAlign: "center", marginLeft: 5, marginTop: 5 }}
              >
                {data.indexOf(line) === 0 && <span>{_(key)}</span>}
                {data.indexOf(line) > 0 ? (
                  <Tooltip placement="top" title={_(key)}>
                    <div>
                      {(() => {
                        if (!line.period) return "-";

                        const value = line.period[key];

                        if (key === "month_enc") {
                          if (!isNaN(value.total)) {
                            return (
                              <>
                                {value.subTree &&
                                  Object.keys(value.subTree).length > 1 && (
                                    <Dropdown
                                      className="dropdown-tree"
                                      menu={{
                                        items: Object.keys(value.subTree).map(
                                          (key, index) => ({
                                            label: (
                                              <div>
                                                <strong>{key}</strong>
                                                <span>
                                                  {`${value.subTree[key]}/12`}
                                                </span>
                                              </div>
                                            ),
                                            key: String(index),
                                          })
                                        ),
                                      }}
                                      // trigger={["click"]}
                                    >
                                      <a onClick={(e) => e.preventDefault()}>
                                        <Space>
                                          <InfoCircleFilledIcon
                                            size={12}
                                            color="#6d7f92"
                                          />
                                        </Space>
                                      </a>
                                    </Dropdown>
                                  )}

                                {value.total}
                                <small
                                  style={{
                                    fontSize: "1.1em",
                                    fontWeight: "400",
                                  }}
                                >
                                  /12
                                </small>
                              </>
                            );
                          }
                          return value.total;
                        }

                        return value;
                      })()}
                    </div>
                  </Tooltip>
                ) : (
                  <></>
                )}
              </div>
            ))}
          </div>
        );
      case "type":
        return (
          <div
            className="column"
            style={{ margin: "0 auto", width: "100%" }}
            onClick={() => setSelectedRow(rowIndex)}
          >
            {Object.keys(line.type).map((key, index) => (
              <div
                className="labled-tag no-border"
                key={index}
                style={{ textAlign: "center", marginLeft: 5, marginTop: 5 }}
              >
                {data.indexOf(line) === 0 ? (
                  <span onClick={() => handleSort(key)}>
                    {_(key)}
                    <div className="sortBy_arrows">
                      <FaArrowDown
                        fill={sortBy[key] == "DESC" ? "#6d7f92" : "#6d7f9250"}
                      />
                      <FaArrowUp
                        fill={sortBy[key] == "ASC" ? "#6d7f92" : "#6d7f9250"}
                      />
                    </div>
                  </span>
                ) : (
                  <Tooltip placement="top" title={_(key)}>
                    <div>
                      {line.type && convertToHoursMinutes(line.type[key])}
                    </div>
                  </Tooltip>
                )}
              </div>
            ))}
          </div>
        );
      case "tasks":
        return (
          <div
            className="column"
            style={{
              display: "flex",
              justifyContent: "start",
              alignItems: "flex-end",
            }}
            onClick={() => setSelectedRow(rowIndex)}
          >
            {line.tasks &&
              line.tasks.map((item, key) => (
                <div
                  className="labled-tag no-border"
                  key={key}
                  style={{
                    justifyContent:
                      data.indexOf(line) === 0 ? "center" : "center",
                    textAlign: "center",
                  }}
                >
                  {data.indexOf(line) === 0 && item.label_fr != "T" && (
                    <span onClick={() => handleSort("task")}>
                      {item.label_fr.substring(0, 15).charAt(0).toUpperCase() +
                        item.label_fr.substring(1, 15)}
                      {item.label_fr.length > 15 ? "..." : ""}
                      {item.label_fr === "Valeur" ? (
                        <div className="sortBy_arrows">
                          <FaArrowDown
                            fill={
                              sortBy["task"] == "DESC" ? "#6d7f92" : "#6d7f9250"
                            }
                          />
                          <FaArrowUp
                            fill={
                              sortBy["task"] == "ASC" ? "#6d7f92" : "#6d7f9250"
                            }
                          />
                        </div>
                      ) : (
                        ""
                      )}
                    </span>
                  )}

                  {data.indexOf(line) > 0 && (
                    <Tooltip placement="top" title={_(item.label_fr)}>
                      <div
                        style={{
                          marginTop: 5,
                          backgroundColor:
                            item.label_fr === "Valeur" && "#dfdfdf",
                        }}
                      >
                        {item.label_fr === "T"
                          ? item.task_duration
                          : convertToHoursMinutes(item.task_duration)}
                      </div>
                    </Tooltip>
                  )}
                </div>
              ))}
          </div>
        );
      case "volumetry":
        return (
          <div
            className="column"
            style={{ margin: "5px 0" }}
            onClick={() => setSelectedRow(rowIndex)}
          >
            {Object.keys(line.volumetry).map((key, index) => (
              <div
                className="labled-tag no-border"
                key={index}
                style={{
                  textAlign: "center",
                  marginLeft: "6px",
                  width: "100%",
                }}
              >
                {data.indexOf(line) === 0 ? (
                  <span onClick={() => handleSort("volumetry")}>
                    {_(`${key}`)}{" "}
                    {key === "total" ? (
                      <div className="sortBy_arrows">
                        <FaArrowDown
                          fill={
                            sortBy["volumetry"] == "DESC"
                              ? "#6d7f92"
                              : "#6d7f9250"
                          }
                        />
                        <FaArrowUp
                          fill={
                            sortBy["volumetry"] == "ASC"
                              ? "#6d7f92"
                              : "#6d7f9250"
                          }
                        />
                      </div>
                    ) : (
                      ""
                    )}
                  </span>
                ) : (
                  <Tooltip placement="top" title={_(key)}>
                    <div
                      style={{ backgroundColor: key === "total" && "#dfdfdf" }}
                    >
                      {line.volumetry[key] !== undefined
                        ? line.volumetry[key]
                        : 0}
                    </div>
                  </Tooltip>
                )}
              </div>
            ))}
          </div>
        );
      case "billing_rate":
        return (
          <div
            className="column"
            style={{ margin: "5px 0" }}
            onClick={() => setSelectedRow(rowIndex)}
          >
            <div
              className="labled-tag no-border"
              style={{ textAlign: "center" }}
            >
              {data.indexOf(line) === 0 ? (
                <span onClick={() => handleSort("billingRate")}>
                  {line["billing_rate"].label}

                  <div className="sortBy_arrows">
                    <FaArrowDown
                      fill={
                        sortBy["billingRate"] == "DESC"
                          ? "#6d7f92"
                          : "#6d7f9250"
                      }
                    />
                    <FaArrowUp
                      fill={
                        sortBy["billingRate"] == "ASC" ? "#6d7f92" : "#6d7f9250"
                      }
                    />
                  </div>
                </span>
              ) : (
                <Tooltip placement="top" title={_(line["billing_rate"].label)}>
                  <div style={{ display: "flex", alignSelf: "center" }}>
                    {line.billing_rate && line.billing_rate.value
                      ? parseFloat(line.billing_rate.value).toFixed(2)
                      : 0}
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
        );
      case "ceiling_compliance_1_6":
        return (
          <div
            className="column"
            style={{ margin: "0 auto", width: "100%" }}
            onClick={() => setSelectedRow(rowIndex)}
          >
            {Object.keys(line.ceiling_compliance_1_6).map((key, index) => (
              <div
                className="labled-tag no-border"
                key={index}
                style={{ textAlign: "center", marginLeft: 5, marginTop: 5 }}
              >
                {data.indexOf(line) === 0 ? (
                  <span
                    onClick={() => handleSort(key)}
                    style={{ cursor: "pointer" }}
                  >
                    {_(key)}
                    {keys.includes(key) ? (
                      <div className="sortBy_arrows">
                        <FaArrowDown
                          fill={sortBy[key] == "DESC" ? "#6d7f92" : "#6d7f9250"}
                        />
                        <FaArrowUp
                          fill={sortBy[key] == "ASC" ? "#6d7f92" : "#6d7f9250"}
                        />
                      </div>
                    ) : (
                      ""
                    )}
                  </span>
                ) : (
                  <Tooltip placement="top" title={_(key)}>
                    {key === "neutral" ? (
                      <div className="editable_zone">
                        <input
                          type="text"
                          className="editable_text"
                          style={{
                            textAlign: "center",
                            width: "50%",
                            marginLeft: -5,
                          }}
                          defaultValue={line.ceiling_compliance_1_6[key]}
                          required={true}
                          onChange={(event) =>
                            handleInputChange(event.target.value, line)
                          }
                          onKeyDown={handleOnEnter}
                        />
                        <BsArrowReturnLeft className="key_enter" />
                      </div>
                    ) : (
                      <div>
                        {key === "time_provision" ? (
                          <>
                            {line.ceiling_compliance_1_6[key] !== "-" ? (
                              parseFloat(line.ceiling_compliance_1_6[key]) <
                              0 ? (
                                <span
                                  style={{
                                    color: "green",
                                    background: "transparent",
                                  }}
                                >
                                  {`${line.ceiling_compliance_1_6[key]}`}
                                </span>
                              ) : (
                                <span
                                  style={{
                                    color: "red",
                                    background: "transparent",
                                  }}
                                >
                                  {`+ ${line.ceiling_compliance_1_6[key]}`}
                                </span>
                              )
                            ) : (
                              "-"
                            )}
                          </>
                        ) : (
                          `${
                            !isNaN(line.ceiling_compliance_1_6[key])
                              ? parseFloat(
                                  line.ceiling_compliance_1_6[key]
                                ).toFixed(2)
                              : line.ceiling_compliance_1_6[key]
                          } ${key === "qp_forfait" ? "%" : ""}`
                        )}
                      </div>
                    )}
                  </Tooltip>
                )}
              </div>
            ))}
          </div>
        );
      default:
        return <></>;
    }
  };

  const handleTitleClients = (title) => {
    const rowIndex = data.findIndex((row) => row.client_id == title.client_id);
    return (
      <Fragment>
        <div
          className="clients_panel"
          style={{
            backgroundColor:
              rowIndex != 0 && rowIndex == selectedRow ? "#18a0fb" : "#fff",
            color: rowIndex != 0 && rowIndex == selectedRow ? "#fff" : "",
          }}
          onClick={() => setSelectedRow(rowIndex)}
        >
          <div
            className="title_infos"
            style={{
              textAlign: data.indexOf(title) === 0 && "center",
              zIndex: 1,
            }}
          >
            {title.client_name.length > 0 ? title.client_name : _("folders")}
          </div>
        </div>
      </Fragment>
    );
  };

  const handleMonthChange = (tab) => {
    setFilter({
      ...filter,
      period: tab,
      start: `${year}-${tab.start}`,
      end: `${year}-${tab.end}`,
    });
  };

  const renderFilterFrame = () => {
    let countFilter = 0;
    filters.forEach((filter) => {
      if (filter && filter.options.values) {
        countFilter++;
      }
    });

    const label = selectedCollaborator?.label || "";
    const [firstName, lastName] = label.split(" ");

    return (
      <div>
        <div
          className="principal-line"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            className="filter-wrapper"
            style={{
              display: "flex",
              alignItems: "center",
              padding: 0,
              margin: 0,
            }}
          >
            {metaData ? (
              <DraggableDialog label={label} metaData={metaData} />
            ) : (
              ""
            )}
            <div className="separator" />
            {renderYearSelect()}
            <div className="filter-frame">
              <div className="additional-line">
                {filter.tabs.map((tab) => {
                  const isDisabled =
                    filter.period &&
                    moment().year() === year &&
                    tab.id > moment().month() + 1;

                  const isActive = filter.period.id === tab.id;
                  return (
                    <div
                      key={tab.id}
                      className={`tag ${isActive ? "active" : ""} ${
                        isDisabled ? "disabled" : ""
                      }`}
                      ref={isActive ? activeMonthRef : null}
                      onClick={() => {
                        if (!isDisabled) {
                          handleMonthChange(tab);
                        }
                      }}
                    >
                      {tab.label}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="separator" />
            <div
              style={{
                color: "#3498db",
              }}
            >
              {`${_("folders")}: ${nbrClients}`}
            </div>
          </div>

          <TTPFilterHorizontalBar
            button
            bg="#FFF"
            subBG="F8F9FA"
            searchBox
            filter={filter}
            filters={filters}
            nbResult={nbrClients}
            countFilter={countFilter}
            pageSize={filter.pageSize}
            updateFilterContent={setFilter}
            cssClass="filter-horizontal-button"
          />
        </div>
      </div>
    );
  };

  const renderYearSelect = () => {
    return (
      <div
        ref={ref}
        className={
          "customed_select" +
          " " +
          (yearDivIsVisible ? "customed_select_opened" : "")
        }
        style={{
          background: "#6d7f92",
          color: "#ffffff",
          zIndex: 2,
        }}
      >
        <div
          className={"customed_select_header"}
          onClick={() => setYearDivIsVisible(!yearDivIsVisible)}
        >
          <span>{year}</span>
          <div className={"actions"}>
            <div>
              <span className={"action"}>
                <TiArrowSortedDown />
              </span>
            </div>
          </div>
        </div>
        {yearDivIsVisible && (
          <div className={"customed_select_body"}>
            <div className={"customed_select_body_options"}>
              {renderTeamYears().map((item, index) => (
                <div
                  key={index}
                  className={"customed_select_body_option"}
                  onClick={() => setYear(item)}
                  style={{ zIndex: 4 }}
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAddBubble = () => {
    return (
      <div className="setting-bubbles" style={{ top: "35%", right: -150 }}>
        <div
          className={`circleSvg 
            ${showSideBar.invoiceBar ? "active opened" : ""}`}
          onClick={() => toggleSideBar("invoiceBar")}
          style={{ height: 50 }}
        >
          <FaFileInvoice height={10} width={10} />
          <span style={{ paddingLeft: "1rem", fontSize: "14px" }}>
            {_("accountingPrestation")}
          </span>
        </div>
      </div>
    );
  };

  const renderNeutralBubble = () => {
    return (
      <div className="setting-bubbles" style={{ top: "40%", right: -150 }}>
        <div
          className={`circleSvg 
            ${showSideBar.neutralBar ? "active opened" : ""}`}
          onClick={() => toggleSideBar("neutralBar")}
          style={{ height: 50 }}
        >
          <MdBlock height={10} width={10} />
          <span style={{ paddingLeft: "1rem", fontSize: "14px" }}>
            {_("neutral")}
          </span>
        </div>
      </div>
    );
  };

  const renderConfigBubble = () => {
    return (
      <div className="setting-bubbles" style={{ top: "45%", right: -150 }}>
        <div
          className={`circleSvg 
            ${showSideBar.configBar ? "active opened" : ""}`}
          onClick={() => toggleSideBar("configBar")}
          style={{ height: 50 }}
        >
          <SettingsIcon height={10} width={10} />
          <span style={{ paddingLeft: "1rem", fontSize: "14px" }}>
            {_("configuration")}
          </span>
        </div>
      </div>
    );
  };

  const renderPrintInvoiceSideBarFrame = () => {
    return (
      <div className="fadePage" style={{ width: "100%" }}>
        <div
          className={`sidebar config-sid ${
            showSideBar.invoiceBar && "display"
          }`}
          style={{ width: "100%", background: "none" }}
        >
          <InvoiceTracking
            token={token}
            collaboratorOptions={collaboratorOptions}
            organization={navCommunity}
            lng={lng}
            year={year}
            onClose={() => toggleSideBar("invoiceBar")}
          />
        </div>
      </div>
    );
  };

  const renderNeutralSideBarFrame = () => {
    return (
      <div className="fadePage" style={{ width: "100%" }}>
        <div
          className={`sidebar config-sid ${
            showSideBar.neutralBar && "display"
          }`}
          style={{ width: "100%", background: "none" }}
        >
          <NeutralReport
            token={token}
            collaboratorOptions={collaboratorOptions}
            organization={navCommunity}
            lng={lng}
            year={year}
            onClose={() => toggleSideBar("neutralBar")}
          />
        </div>
      </div>
    );
  };

  const renderConfigSideBarFrame = () => {
    return (
      <div className="fadePage" style={{ width: "100%" }}>
        <div
          className={`sidebar config-sid ${showSideBar.configBar && "display"}`}
          style={{ width: "100%", background: "none" }}
        >
          <InvoiceConfig
            token={token}
            organization={navCommunity}
            onClose={() => toggleSideBar("configBar")}
          />
        </div>
      </div>
    );
  };

  const renderTotalCards = () => {
    const totalTimeEtalon = totalData.tasks[3]["prest_value"] * 3600;
    const chartData = Object.keys(totalData.type).map((key) => ({
      name: [key],
      value: totalData.type[key],
    }));
    const COLORS = ["#ea8124", "#06d9b1", "#18a0fb", "#6d7f92", "#29394d"];

    const RADIAN = Math.PI / 180;
    const renderCustomizedLabel = ({
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      percent,
      index,
    }) => {
      const radius = innerRadius + (outerRadius - innerRadius) * 0.4;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);

      return (
        <text
          x={x}
          y={y}
          fill="#29394d"
          fontSize={12}
          textAnchor={x > cx ? "start" : "end"}
          dominantBaseline="central"
        >
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      );
    };
    return (
      <div className="scroll-container">
        <button className="scroll-btn left" onClick={() => scrollCards("left")}>
          <FaChevronLeft />
        </button>
        <div className="cards_wrapper" ref={cardsWrapperRef}>
          {/* Total Volumetry */}
          <div className="total_card">
            <div className="card_title">
              <h3>Caractéristiques encodage (p)</h3>
              <div>{`${totalData.volumetry.total}`}</div>
            </div>
            <div className="card_body">
              <div className="wrapper">
                {Object.keys(totalData.volumetry)
                  .filter((key) => key !== "total")
                  .map((key) => (
                    <div key={key}>
                      <p>{_(key)}</p>
                      <strong>{totalData.volumetry[key]}</strong>
                      <span>{`${totalData.sum.volumetry.time_etalon[key]}s`}</span>
                    </div>
                  ))}
              </div>
              <div className="divider" />
            </div>
            <div className="card_title">
              <h3>Temps étalon total (s)</h3>
              <div>{`${totalTimeEtalon.toFixed(1)}`}</div>
            </div>
            <div className="card_body">
              <div className="progress-bar">
                {Object.keys(totalData.volumetry)
                  .filter((key) => key !== "total")
                  .map((key, index) => (
                    <div
                      key={key}
                      className="bar"
                      style={{
                        width: `${
                          (totalData.sum.volumetry.time_etalon_total[key] *
                            100) /
                          totalTimeEtalon
                        }%`,
                        backgroundColor: COLORS[index % COLORS.length],
                      }}
                      value={totalData.sum.volumetry.time_etalon_total[key]}
                    />
                  ))}
              </div>
            </div>
          </div>
          {/* Total TimeSheet */}
          <div className="total_card">
            <div className="card_title">
              <h3>TimeSheet</h3>
              <div>{`${convertToHoursMinutes(
                totalData.tasks[1].task_duration
              )}`}</div>
            </div>
            <div className="card_body">
              <div className="wrapper">
                {totalData.tasks
                  .filter((t) => !["T", "Valeur"].includes(t.label_fr))
                  .map((t) => (
                    <div key={t.label_fr}>
                      <Tooltip placement="top" title={t.label_fr}>
                        <p>{_(t.label_fr.substring(0, 3))}</p>
                      </Tooltip>
                      <strong>{convertToHoursMinutes(t.task_duration)}</strong>
                      <span>{`${t.percentage}%`}</span>
                    </div>
                  ))}
              </div>
              <div className="divider" />
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 20,
                  width: "100%",
                  height: 100,
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart width={100} height={100}>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={40}
                      fill="#8884d8"
                      dataKey="value"
                      minAngle={5}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div
                  className="wrapper"
                  style={{
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    flex: "none",
                    gap: 0,
                  }}
                >
                  {Object.keys(totalData.type).map((key, index) => (
                    <div
                      className="sub-wrapper"
                      style={{ "--legend-bg": COLORS[index % COLORS.length] }}
                    >
                      <p>{_(key)}</p>
                      <strong>
                        {convertToHoursMinutes(totalData.type[key])}
                      </strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          {/* Average Encoding Time */}
          <div className="total_card">
            <div className="card_title">
              <h3>Respect de temps d'encodage</h3>
            </div>
            <div className="card_body">
              <div className="wrapper">
                <div>
                  <p>Temps étalon moyen/u</p>
                  <strong>{`${totalData.tasks[1].prest_value.toFixed(
                    2
                  )}s`}</strong>
                </div>
              </div>
              <div className="wrapper">
                <div>
                  <p>Temps d'encodage/u</p>
                  <strong>{`${totalData.tasks[1].ts_value.toFixed(
                    2
                  )}s`}</strong>
                </div>
              </div>
              <div className="wrapper">
                <div>
                  <p>Ratio de respect</p>
                  <strong style={{ color: "#18a0fb" }}>{`${(
                    totalData.tasks[3].enc_value * 100
                  ).toFixed(1)}%`}</strong>
                </div>
              </div>
            </div>
          </div>
          {/* Total Billing */}
          <div className="total_card">
            <div className="card_title">
              <h3>Facturation</h3>
              <div
                style={{
                  color:
                    totalData.billing_rate.diff >= 0 ? "#02af8e" : "#de4848",
                  backgroundColor:
                    totalData.billing_rate.diff >= 0
                      ? "#02af8e20"
                      : "#de484820",
                }}
              >{`${totalData.billing_rate.diff >= 0 ? "+" : ""} ${formatAmount(
                totalData.billing_rate.diff
              )}`}</div>
            </div>
            <div className="card_body">
              <div className="wrapper">
                <div>
                  <p>∑ honoraires</p>
                  <strong>{formatAmount(totalData.billing_rate.value)}</strong>
                </div>
              </div>
              <div className="wrapper">
                <div>
                  <p>∑ honoraires + ∆%productivité</p>
                  <strong>
                    {formatAmount(totalData.billing_rate.fee_prod)}
                  </strong>
                </div>
              </div>
              <div className="wrapper">
                <div>
                  <p>Ajustement</p>
                  <strong>
                    {formatAmount(totalData.billing_rate.fee_adjust)}
                  </strong>
                </div>
              </div>
            </div>
          </div>
          {/* Total Ceiling Compliance 1/6 */}
          <div className="total_card">
            <div className="card_title">
              <h3>{_("Plafond en respect de la norme 1/6")}</h3>
            </div>
            <div className="card_body">
              <div className="wrapper ceiling_wrapper">
                {Object.keys(totalData.ceiling_compliance_1_6).map((key) => (
                  <div key={key}>
                    <p>{_(key)}</p>
                    <strong>
                      {!isNaN(totalData.ceiling_compliance_1_6[key])
                        ? totalData.ceiling_compliance_1_6[key].toFixed(1)
                        : totalData.ceiling_compliance_1_6[key]}
                    </strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <button
          className="scroll-btn right"
          onClick={() => scrollCards("right")}
        >
          <FaChevronRight />
        </button>
      </div>
    );
  };

  return (
    <div
      className="page-content"
      style={{ background: "none", margin: 0, padding: 0 }}
    >
      {renderAddBubble()}
      {renderNeutralBubble()}
      {renderConfigBubble()}
      <div className="ttp-tab-container" style={{ margin: 0, padding: 5 }}>
        <div
          className="content-body large-content"
          style={{ margin: 0, padding: 5, width: "100%" }}
        >
          {renderFilterFrame()}
          <InvoiceDownloader
            token={token}
            navCommunity={navCommunity}
            data={collaboratorOptions}
            open={open}
            loading={isLoading}
            close={() => setOpen(false)}
          />
          {loading ? (
            <div className="ripple_loading">
              <img src={"/img/Ripple-1s-150px.svg"} alt="" />
            </div>
          ) : data && data.length > 0 && filter.collaborator ? (
            <div
              className="custumed_matrix_container"
              style={{
                position: "relative",
                marginTop: 10,
                padding: 0,
                height: "80vh",
                overflowY: "auto",
              }}
            >
              {renderTotalCards()}
              <div className="prev_arrow" onClick={scrollLeft}>
                <IconArrowRight fill="#29394d" size="15" />
              </div>
              <div
                ref={scrollRef}
                style={{
                  overflowX: "auto",
                  marginTop: 10,
                }}
              >
                <div
                  style={{
                    width: "256%",
                    overflowX: "auto",
                  }}
                >
                  <CustumedMatrix
                    columns={columnsClientsInformations}
                    items={data}
                    handleCellule={handleColumns}
                    handleTitle={(title, index) =>
                      handleTitleClients(title, index)
                    }
                    headerTagBG={"#FFFFFF"}
                  />
                </div>
              </div>
              <div className="next_arrow" onClick={scrollRight}>
                <IconArrowRight fill="#29394d" size="15" />
              </div>
              {renderPaginate()}
              {showSideBar.invoiceBar && renderPrintInvoiceSideBarFrame()}
              {showSideBar.neutralBar && renderNeutralSideBarFrame()}
              {showSideBar.configBar && renderConfigSideBarFrame()}
            </div>
          ) : (
            <div className="no_data">
              <div className="title">{_("noDataFound")}</div>
              <div className="subTitle">{_("noReportingsFound")}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrestationSuivi;
